import type { AttachmentUpload } from '@/api/attachments';
import type { LlmConfig } from '@/api/llmConfig';

/**
 * Attachment states in the composer (spec §3): validating, uploading with
 * a percentage, complete, failed, unsupported and reattachment required
 * (the file was being sent when the page was reloaded, so the browser no
 * longer has it). Cancelled uploads stay listed until removed or retried.
 */

export type UploadState =
  | 'validating'
  | 'uploading'
  | 'complete'
  | 'failed'
  | 'unsupported'
  | 'reattach'
  | 'cancelled';

export interface UploadItem {
  /** Local id. */
  id: string;
  name: string;
  size: number;
  state: UploadState;
  percent: number;
  /** The server's explanation, already in the UI language. */
  message: string | null;
  /** A dictionary key (with values) when the browser explains it. */
  reasonKey: string | null;
  reasonValues?: Record<string, string | number>;
  /** Retry may help (network, storage or conversion failures). */
  retryable: boolean;
  /** The attachment id once the server accepted it. */
  fileId: number | null;
}

/** Busy items count as pending sources until they reach the server. */
export const isInProgress = (item: UploadItem) =>
  item.state === 'validating' || item.state === 'uploading';

export const extensionOf = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
};

/**
 * The browser's own check before anything is sent: format by extension,
 * empty files and the per-file limit, from GET /llm/config. The server
 * checks the content again (encryption, damage, mismatched content).
 */
export const validateLocally = (
  file: { name: string; size: number },
  config: Pick<LlmConfig, 'uploads' | 'bytes_per_mb'>,
): Pick<UploadItem, 'state' | 'reasonKey' | 'reasonValues'> | null => {
  const extension = extensionOf(file.name);
  if (!config.uploads.accepted_extensions.includes(extension)) {
    return {
      state: 'unsupported',
      reasonKey: 'v2.attachments.errType',
      reasonValues: { e: extension || '?' },
    };
  }
  if (file.size === 0) {
    return { state: 'failed', reasonKey: 'v2.attachments.errEmpty' };
  }
  if (file.size > config.uploads.max_bytes_per_file) {
    return {
      state: 'failed',
      reasonKey: 'v2.attachments.errTooLarge',
      reasonValues: {
        m: String(
          Number(
            (config.uploads.max_bytes_per_file / config.bytes_per_mb).toFixed(
              2,
            ),
          ),
        ),
      },
    };
  }
  return null;
};

/** Server reasons that mean a different file is needed, not another try. */
const UNSUPPORTED_REASONS = new Set(['unsupported_format', 'content_mismatch']);
const RETRYABLE_REASONS = new Set(['storage_failed', 'conversion_failed']);

/** The item after the upload request ended. */
export const afterUpload = (
  item: UploadItem,
  result: AttachmentUpload,
): UploadItem => {
  switch (result.kind) {
    case 'cancelled':
      return { ...item, state: 'cancelled', percent: 0, retryable: true };
    case 'network':
      return {
        ...item,
        state: 'failed',
        reasonKey: 'v2.attachments.errNetwork',
        message: null,
        retryable: true,
      };
    case 'error':
      return {
        ...item,
        state: 'failed',
        message: result.message,
        reasonKey: result.message ? null : 'v2.attachments.errRead',
        retryable: result.status >= 500 || result.status === 0,
      };
    default: {
      const outcome = result.outcome;
      if (outcome.status === 'accepted') {
        return {
          ...item,
          state: 'complete',
          percent: 100,
          fileId: outcome.file_id,
          message: null,
          reasonKey: null,
          retryable: false,
        };
      }
      const reason = outcome.reason ?? '';
      return {
        ...item,
        state: UNSUPPORTED_REASONS.has(reason) ? 'unsupported' : 'failed',
        message: outcome.message,
        reasonKey: outcome.message ? null : 'v2.attachments.errRead',
        retryable: RETRYABLE_REASONS.has(reason),
      };
    }
  }
};

/** Status keys per state for the row (the reason, when known, wins). */
export const STATE_KEYS: Record<UploadState, string> = {
  validating: 'v2.attachments.att_validating',
  uploading: 'v2.attachments.att_uploading',
  complete: 'v2.attachments.att_ready',
  failed: 'v2.attachments.att_failed',
  unsupported: 'v2.attachments.att_unsupported',
  reattach: 'v2.attachments.reattachHint',
  cancelled: 'v2.attachments.att_cancelled',
};
